'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { useThemeConfigStore } from '@store/theme-config-store';
import { useWhiteLabelDestinationStore } from '@store/white-label-destination-store';
import {
  isPublicPartnerConfig,
  normalizePartnerSlug,
  parsePartnerSlug,
  type PublicPartnerConfig,
} from '@v2/action/white-label/public-partner-config';

const resetPartnerTheme = () => {
  useThemeConfigStore.getState().resetThemeConfig();
  useWhiteLabelDestinationStore.getState().reset();
};

const applyPartnerTheme = (config: PublicPartnerConfig) => {
  useThemeConfigStore.setState({
    primaryColor: config.primaryColor,
    cities: config.citiesSection,
    isTenantEnabled: true,
    tenantSlug: config.slug,
    lightLogo: config.lightLogo,
    darkLogo: config.darkLogo,
    sublogoUrl: config.sublogoUrl,
  });
  useWhiteLabelDestinationStore
    .getState()
    .setSportsDestination(config.sportsDestination);
};

export function EventPartnerTheme() {
  const searchParams = useSearchParams();
  const partnerSlug = parsePartnerSlug(searchParams?.get('partnerSlug'));

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    resetPartnerTheme();

    if (!partnerSlug) {
      return () => {
        controller.abort();
        resetPartnerTheme();
      };
    }

    const loadPartnerTheme = async () => {
      try {
        const response = await fetch(
          `/api/partners/${encodeURIComponent(partnerSlug)}`,
          {
            cache: 'no-store',
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          return;
        }

        const config: unknown = await response.json();

        if (
          isActive &&
          isPublicPartnerConfig(config) &&
          normalizePartnerSlug(config.slug) === partnerSlug
        ) {
          applyPartnerTheme(config);
        }
      } catch {
        if (!controller.signal.aborted) {
          resetPartnerTheme();
        }
      }
    };

    void loadPartnerTheme();

    return () => {
      isActive = false;
      controller.abort();
      resetPartnerTheme();
    };
  }, [partnerSlug]);

  return null;
}
